import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from '../../components/AppIcon';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import SystemStatus from './components/SystemStatus';
import LDAPConnectionIndicator from './components/LDAPConnectionIndicator';
import LoginAttemptMonitor from './components/LoginAttemptMonitor';
import { useAuth } from '../../contexts/AuthContext';

const UserAuthenticationAndLogin = () => {
  const navigate = useNavigate();
  const { signIn, user, userProfile, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    status: 'online',
    message: 'Alle Systeme betriebsbereit'
  });
  const [ldapConnection, setLdapConnection] = useState({
    connected: true,
    lastSync: '26.11.2025 19:00'
  });

  const maxAttempts = 5;
  const lockoutTime = 15;

  // Demo user for quick login (passwordless)
  const QUICK_LOGIN_USER = {
    email: 'demo.mitarbeiter@arbeitsagentur.de',
    password: 'Demo2025!Secure'
  };

  // Auto-login effect
  useEffect(() => {
    const attemptAutoLogin = async () => {
      if (autoLoginAttempted || user || authLoading) return;
      
      const savedCredentials = localStorage.getItem('ba_auto_login');
      if (savedCredentials) {
        try {
          const credentials = JSON.parse(savedCredentials);
          if (credentials?.email && credentials?.password) {
            setAutoLoginAttempted(true);
            setLoading(true);
            
            const { data, error: signInError } = await signIn(
              credentials?.email, 
              credentials?.password
            );
            
            if (signInError) {
              // Auto-login failed, clear credentials
              localStorage.removeItem('ba_auto_login');
              setError('Automatische Anmeldung fehlgeschlagen. Bitte melden Sie sich manuell an.');
            }
          }
        } catch (err) {
          console.error('Auto-login error:', err);
          localStorage.removeItem('ba_auto_login');
        } finally {
          setLoading(false);
        }
      } else {
        setAutoLoginAttempted(true);
      }
    };

    attemptAutoLogin();
  }, [signIn, user, authLoading, autoLoginAttempted]);

  useEffect(() => {
    // Redirect if already authenticated
    if (user && userProfile) {
      const redirectPath = userProfile?.role === 'supervisor' ?'/supervisor-dashboard-and-analytics' 
        : userProfile?.role === 'administrator' ?'/system-administration-panel' :'/case-worker-dashboard';
      navigate(redirectPath);
    }
  }, [user, userProfile, navigate]);

  const handleQuickLogin = async () => {
    if (isLocked) {
      setError(`Ihr Konto ist für ${lockoutTime} Minuten gesperrt. Bitte kontaktieren Sie den IT-Helpdesk.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use demo credentials for quick login
      const { data, error: signInError } = await signIn(
        QUICK_LOGIN_USER?.email,
        QUICK_LOGIN_USER?.password
      );

      if (signInError) {
        throw new Error(signInError?.message || 'Schnellanmeldung fehlgeschlagen');
      }

      if (data?.user) {
        // Reset login attempts on successful login
        setLoginAttempts(0);
      }
    } catch (err) {
      setError(`Schnellanmeldung nicht möglich: ${err?.message || 'Unbekannter Fehler'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (formData) => {
    if (isLocked) {
      setError(`Ihr Konto ist für ${lockoutTime} Minuten gesperrt. Bitte kontaktieren Sie den IT-Helpdesk.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await signIn(formData?.email, formData?.password);

      if (signInError) {
        throw new Error(signInError?.message || 'Anmeldung fehlgeschlagen');
      }

      if (data?.user) {
        // Reset login attempts on successful login
        setLoginAttempts(0);
        
        // Navigation will happen via useEffect when userProfile loads
      }
    } catch (err) {
      // Failed authentication
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      if (newAttempts >= maxAttempts) {
        setIsLocked(true);
        setError(`Zu viele fehlgeschlagene Anmeldeversuche. Ihr Konto wurde für ${lockoutTime} Minuten gesperrt.`);
        
        // Auto-unlock after lockout time
        setTimeout(() => {
          setIsLocked(false);
          setLoginAttempts(0);
        }, lockoutTime * 60 * 1000);
      } else {
        setError(`${err?.message || 'Ungültige Anmeldedaten'}. (${maxAttempts - newAttempts} Versuche verbleibend)`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || (loading && !autoLoginAttempted)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#4DD0E1] to-[#FF9800] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">
            {autoLoginAttempted ? 'Authentifizierung läuft...' : 'Automatische Anmeldung...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#4DD0E1] to-[#FF9800] flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding & Information */}
        <div className="hidden lg:flex flex-col justify-center space-y-8 p-8">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <Icon name="Building2" size={32} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  Bundesagentur für Arbeit
                </h1>
                <p className="text-lg text-white/90">
                  Mitarbeiterportal
                </p>
              </div>
            </div>
            
            <p className="text-white/95 text-lg leading-relaxed">
              Sicherer Zugang zum internen Kundenverwaltungssystem für Mitarbeiter der Bundesagentur für Arbeit
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg shadow-sm border border-white/20">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  Höchste Sicherheitsstandards
                </h3>
                <p className="text-sm text-white/90">
                  LDAP/Active Directory Integration mit Multi-Faktor-Authentifizierung
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg shadow-sm border border-white/20">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Lock" size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  DSGVO-konform
                </h3>
                <p className="text-sm text-white/90">
                  Vollständige Einhaltung der Datenschutz-Grundverordnung und BDSG
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg shadow-sm border border-white/20">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Clock" size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  Flexible Sitzungsverwaltung
                </h3>
                <p className="text-sm text-white/90">
                  Wählen Sie Ihre bevorzugte Sitzungsdauer für optimale Sicherheit
                </p>
              </div>
            </div>
          </div>

          <SecurityBadges />
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#4DD0E1] to-[#26C6DA] p-8 text-white">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <Icon name="LogIn" size={32} className="text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-center mb-2">
                Mitarbeiteranmeldung
              </h2>
              <p className="text-white/90 text-center text-sm">
                Schnellanmeldung oder mit Ihren Zugangsdaten
              </p>
            </div>

            {/* Form Container */}
            <div className="p-8 space-y-6">
              {/* System Status */}
              <SystemStatus 
                status={systemStatus?.status} 
                message={systemStatus?.message} 
              />

              {/* LDAP Connection */}
              <LDAPConnectionIndicator 
                connected={ldapConnection?.connected}
                lastSync={ldapConnection?.lastSync}
              />

              {/* Login Attempt Monitor */}
              <LoginAttemptMonitor
                attempts={loginAttempts}
                maxAttempts={maxAttempts}
                lockoutTime={lockoutTime}
              />

              {/* Login Form with Quick Login */}
              <LoginForm 
                onSubmit={handleLogin}
                onQuickLogin={handleQuickLogin}
                loading={loading}
                error={error}
              />

              {/* Help Section */}
              <div className="pt-6 border-t border-slate-200">
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                  <Icon name="HelpCircle" size={16} />
                  <span>Benötigen Sie Hilfe?</span>
                </div>
                <div className="flex items-center justify-center gap-4 mt-3">
                  <button className="text-sm text-[#4DD0E1] hover:text-[#26C6DA] hover:underline flex items-center gap-1">
                    <Icon name="Phone" size={14} />
                    IT-Helpdesk
                  </button>
                  <span className="text-slate-300">|</span>
                  <button className="text-sm text-[#4DD0E1] hover:text-[#26C6DA] hover:underline flex items-center gap-1">
                    <Icon name="Mail" size={14} />
                    Support kontaktieren
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-8 py-4 border-t border-slate-200">
              <p className="text-xs text-center text-slate-600">
                © {new Date()?.getFullYear()} Bundesagentur für Arbeit. Alle Rechte vorbehalten.
              </p>
              <div className="flex items-center justify-center gap-4 mt-2">
                <button className="text-xs text-slate-500 hover:text-slate-700">
                  Datenschutz
                </button>
                <span className="text-slate-300">•</span>
                <button className="text-xs text-slate-500 hover:text-slate-700">
                  Impressum
                </button>
                <span className="text-slate-300">•</span>
                <button className="text-xs text-slate-500 hover:text-slate-700">
                  Nutzungsbedingungen
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Branding */}
          <div className="lg:hidden mt-8 text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Icon name="Building2" size={24} className="text-white" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-white">
                  Bundesagentur für Arbeit
                </h3>
                <p className="text-sm text-white/90">
                  Mitarbeiterportal
                </p>
              </div>
            </div>
            <SecurityBadges />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAuthenticationAndLogin;